import * as Yup from "yup";
import jexl from "jexl";
import {
  canadaAddressSchema,
  dateOfBirthSchema,
  phoneMobileSchema,
  firstNationStatusDetailSchema,
  familyMembersSchema,
  mapLocationSchema,
  bankDetailsSchema,
} from "../../common/constants/schemas";
import { yupToFormErrors } from "formik";
import {
  fieldTypes,
  getDefaultValue,
  fieldSubTypes,
  isEditableField,
  isContainer,
  isWidget,
} from "./fieldTypes";
import { validationConstants } from "../constants/validations";
import { evaluate, phoneRegExp, poBoxRegExp } from "../../utils/globalUtility";
import { getBankNameFromNumber } from "../../utils/bankValidation";
import { getVisibleFields } from "../../utils/uiUtility";

const fileRequiredSchema = Yup.object().shape({
  id: Yup.string(validationConstants.invalid).required(
    validationConstants.required,
  ),
  name: Yup.string(validationConstants.invalid).required(
    validationConstants.required,
  ),
});

const fileSchema = Yup.object().shape({
  id: Yup.string(validationConstants.invalid).required(
    validationConstants.required,
  ),
  name: Yup.string(validationConstants.invalid),
});

const isEmpty = (value) => {
  if (!value) {
    return true;
  }

  if (Array.isArray(value) && value?.length === 0) {
    return true;
  }

  if (typeof value === "object") {
    const keys = Object.keys(value ?? {});
    if (!keys || keys?.length === 0) {
      return true;
    }

    for (var i = 0; i < keys?.length; i++) {
      const empty = isEmpty(value[keys[i]]);

      if (!empty) {
        return false;
      }
    }

    return true;
  }

  if (!value || value === "" || value.toString().trim() === "") {
    return true;
  }

  return false;
};

export const scrollToError = (form, data, context, errors) => {
  // If there are errors and this validation was triggered by form submission
  // (you can detect this by checking if all fields are being validated at once)
  if (Object.keys(errors).length > 0) {
    // Add a small delay to ensure DOM updates are complete
    setTimeout(async () => {
      const errorFieldNames = Object.keys(errors);

      // Get visual order of fields by reading the dynamic form fields
      const visibleFieldsInOrder = await getVisibleFields(
        data,
        form?.fields,
        context,
        context.getRefData,
      );

      // First error field based on visual order instead of error order
      let firstErrFieldId = visibleFieldsInOrder.find((field) =>
        errorFieldNames.includes(field),
      );

      // Change first error for some widgets
      if (firstErrFieldId) {
        if (firstErrFieldId == "dob") {
          firstErrFieldId = "dd";
        }
        if (firstErrFieldId == "mapLat" || firstErrFieldId == "mapLng") {
          firstErrFieldId = "googleMap";
        }

        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          // Find the error field
          const firstErrorField =
            document.querySelector(`[name="${firstErrFieldId}"]`) ||
            document.querySelector(`[id="${firstErrFieldId}"]`);
          if (firstErrorField) {
            firstErrorField.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            firstErrorField.focus();
          }
        });
      }
    });
  }
};

export const validateInput = async ({ form, data, context }) => {
  let validationErrors = {};

  validationErrors = await validateInputs(
    form.fields,
    data,
    context,
    validationErrors,
  );
  return validationErrors;
};

const validateInputs = async (fields, data, context, validationErrors) => {
  for (var i = 0; i < fields?.length; i++) {
    const field = fields[i];

    // Check if field is hidden by condition
    if (field.condition) {
      const isHidden = evaluate(field.condition, context, data);
      if (field.condition.action === 'hidden' && isHidden) {
        continue; // Skip validation for hidden fields
      }
    }

    if (!isEditableField(field.type)) {
      if (isContainer(field.type)) {
        validationErrors = await validateInputs(
          field.fields,
          data,
          context,
          validationErrors,
        );
      }

      continue;
    }
    if (isWidget(field.type)) {
      let validationSchema = null;
      let conditionValue = true;
      if (field.validations?.condition?.expression) {
        conditionValue = evaluate(field.validations.condition, context, data);
      }
      if (conditionValue) {
        switch (field.type) {
          case fieldTypes.dateWidget:
            validationSchema = dateOfBirthSchema;
            break;
          case fieldTypes.phoneWidget:
            validationSchema = phoneMobileSchema;
            break;
          case fieldTypes.addressWidget:
            validationSchema = canadaAddressSchema;
            break;
          case fieldTypes.firstNationStatusWidget:
            validationSchema = firstNationStatusDetailSchema;
            break;
          case fieldTypes.familyMembersWidget:
            validationSchema = familyMembersSchema;
            break;
          case fieldTypes.mapLocationWidget:
            validationSchema = mapLocationSchema;
            break;
          case fieldTypes.bankDetailsWidget:
            validationSchema = bankDetailsSchema;
            break;
        }
      }
      if (!!validationSchema) {
        const widgetErrors = await validateWidget({
          data,
          validationSchema,
          properties: field.properties,
          isProxyUser: context.user.isProxyUser,
        });
        validationErrors = { ...validationErrors, ...widgetErrors };
      }
      continue;
    }

    validationErrors = {
      ...validationErrors,
      ...validateField({ data, field, context }),
    };
  }

  return validationErrors;
};

const isPoBoxMismatchValid = (isPOBox, streetAddressLine1) => {
  if (!streetAddressLine1 || isPOBox !== "N") return true;
  return !poBoxRegExp.test(streetAddressLine1);
};

const validateField = ({ data, field, context }) => {
  const errors = {};

  const { validations = {}, type } = field ?? {};

  const fieldValue = data
    ? (data[field.properties.name] ?? getDefaultValue(type))
    : getDefaultValue(type);

  const {
    condition,
    required = false,
    pattern,
    min,
    max,
    minLength,
    maxLength,
    oneOf,
    custom,
  } = validations;

  let conditionValue = true;
  if (condition?.expression) {
    conditionValue = evaluate(condition, context, data);
  }
  if (conditionValue) {
    // Required validation
    if (required && isEmpty(fieldValue)) {
      errors[field.properties.name] =
        required.text ?? validationConstants.required;
      return errors;
    }

    // Pattern validation (e.g., regex)
    if (pattern?.value) {
      let patternValue = pattern.value;
      if (patternValue.startsWith("patternValidations.")) {
        const exprPattern = jexl.evalSync(pattern?.value, context);
        if (exprPattern) {
          patternValue = exprPattern;
        }
      }
      if (!new RegExp(patternValue).test(fieldValue)) {
        errors[field.properties.name] =
          pattern.text ?? validationConstants.patternMismatch;
        return errors;
      }
    }

    // Min/Max validation (numeric fields)
    if (min?.value !== undefined && fieldValue < min?.value) {
      errors[field.properties.name] = min.text ?? validationConstants.minValue;
      return errors;
    }
    if (max?.value !== undefined && fieldValue > max.value) {
      errors[field.properties.name] = max.text ?? validationConstants.maxValue;
      return errors;
    }

    // MinLength/MaxLength validation (for string fields)
    if (
      minLength?.value !== undefined &&
      fieldValue?.length < minLength.value
    ) {
      errors[field.properties.name] =
        minLength.text ?? validationConstants.minLength;
      return errors;
    }
    if (
      maxLength?.value !== undefined &&
      fieldValue?.length > maxLength.value
    ) {
      errors[field.properties.name] =
        maxLength.text ?? validationConstants.maxLength;
      return errors;
    }

    // oneOf validation
    if (oneOf) {
      if (
        oneOf.value &&
        Array.isArray(oneOf.value) &&
        !oneOf.value.includes(fieldValue)
      ) {
        errors[field.properties.name] = oneOf.text; // ?? validationConstants.oneOf;
        return errors;
      }
      if (
        oneOf.field &&
        data[oneOf.field] &&
        data[oneOf.field] !== fieldValue
      ) {
        errors[field.properties.name] = oneOf.text; // ?? validationConstants.oneOf;
        return errors;
      }
    }

    if (custom) {
      const methods = {
        getBankNameFromNumber,
        isPoBoxMismatchValid,
      };

      const method = methods[custom.method];
      if (method) {
        const evaluatedParams = Object.keys(custom.params ?? []).map((key) => {
          return jexl.evalSync(
            custom.params[key],
            !!custom.stepName
              ? context[custom.stepName]
              : { ...context, form: data || {} },
          );
        });

        if (!method(...evaluatedParams)) {
          errors[field.properties.name] = custom.text; // ?? validationConstants.oneOf;
          return errors;
        }
      }
    }

    // Type validation (if applicable)
    if (!validateValueType({ field, value: fieldValue })) {
      errors[field.properties.name] =
        validations.text ?? validationConstants.invalid;
      return errors;
    }
  }

  return errors;
};

const validateValueType = ({ field, value }) => {
  if (isEmpty(value)) {
    return true;
  }
  const { type } = field;

  if (type === fieldTypes.check) {
    return Yup.boolean().isValid(value);
  }

  if (type === fieldTypes.dropdown) {
    return Yup.string().isValid(value);
  }

  if (type === fieldTypes.file) {
    return fileSchema.isValid(value);
  }

  if (type === fieldTypes.date) {
    return Yup.date().isValid(value);
  }

  if (type === fieldTypes.number) {
    return Yup.number().isValid(value);
  }

  if (type === fieldTypes.input) {
    const subType = field.properties.type;
    if (subType === fieldSubTypes.email) {
      return Yup.string().email().isValid(value);
    }

    if (subType === fieldSubTypes.phone) {
      return Yup.string().matches(phoneRegExp, "").isValid(value);
    }
  }

  return true;
};

const validateWidget = async ({
  data,
  validationSchema,
  properties,
  isProxyUser,
}) => {
  try {
    await validationSchema.validate(data, {
      abortEarly: false,
      context: { ...properties, isProxyUser },
    });
  } catch (e) {
    return yupToFormErrors(e);
  }
};
